import { useEffect, useState } from "react"
import { cn } from "@/lib/utils"

/* Returns `true` only after `flag` has been continuously `true` for `delayMs`.
 * Use this to gate skeleton/spinner UI so it never flashes for sub-150ms loads.
 *
 *   const loading = ...
 *   const showLoader = useDelayedLoading(loading, 150)
 *   if (showLoader) return <PageLoader />
 *
 * If `flag` flips back to false before the delay elapses we never render the
 * loader at all.
 */
export function useDelayedLoading(flag: boolean, delayMs = 150): boolean {
  const [show, setShow] = useState(false)
  useEffect(() => {
    if (!flag) {
      setShow(false)
      return
    }
    const t = setTimeout(() => setShow(true), delayMs)
    return () => clearTimeout(t)
  }, [flag, delayMs])
  return show
}

interface SpinnerProps {
  className?: string
}

export function SpinnerIcon({ className }: SpinnerProps) {
  return (
    <svg
      className={cn("h-4 w-4 animate-spin text-current", className)}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
        className="opacity-25"
      />
      <path
        d="M4 12a8 8 0 018-8"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        className="opacity-75"
      />
    </svg>
  )
}

export function PageLoader({ label = "Loading…" }: { label?: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="min-h-[40vh] flex flex-col items-center justify-center gap-3 text-[color:var(--color-text-muted)]"
    >
      <SpinnerIcon className="h-8 w-8 text-[color:var(--color-accent)]" />
      <p className="text-sm">{label}</p>
    </div>
  )
}

interface CardLoaderProps {
  className?: string
}

export function CardLoader({ className }: CardLoaderProps) {
  return (
    <div
      className={cn(
        "rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-card p-5 shadow-[var(--shadow-sm)]",
        className,
      )}
    >
      <div className="flex items-start justify-between">
        <div className="h-10 w-10 rounded-[var(--radius-md)] bg-[var(--color-bg-muted)] animate-pulse" />
        <div className="h-2.5 w-20 rounded bg-[var(--color-bg-muted)] animate-pulse" />
      </div>
      <div className="mt-4 h-8 w-24 rounded bg-[var(--color-bg-muted)] animate-pulse" />
      <div className="mt-2 h-2 w-32 rounded bg-[var(--color-bg-muted)] animate-pulse" />
    </div>
  )
}

interface TableLoaderProps {
  rows?: number
  cols?: number
  className?: string
}

export function TableLoader({ rows = 5, cols = 4, className }: TableLoaderProps) {
  return (
    <div
      className={cn(
        "rounded-[var(--radius-lg)] border border-[var(--color-border)] overflow-hidden bg-card",
        className,
      )}
      role="status"
      aria-live="polite"
      aria-label="Loading table"
    >
      <div
        className="grid border-b border-[var(--color-border)] bg-[var(--color-bg-subtle)]"
        style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
      >
        {Array.from({ length: cols }).map((_, i) => (
          <div key={i} className="h-10 flex items-center px-4">
            <div className="h-2 w-16 rounded bg-[var(--color-border)] animate-pulse" />
          </div>
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div
          key={r}
          className="grid border-b border-[var(--color-border)] last:border-b-0"
          style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
        >
          {Array.from({ length: cols }).map((_, c) => (
            <div key={c} className="h-12 flex items-center px-4">
              <div
                className="h-3 rounded bg-[var(--color-bg-muted)] animate-pulse"
                style={{
                  width: `${50 + ((r * cols + c) % 4) * 12}%`,
                  animationDelay: `${(r * cols + c) * 60}ms`,
                }}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}
